import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, timeAgo } from "@/lib/api";
import { Calendar, Users, ListChecks, UserCircle, Newspaper, MapPin } from "lucide-react";
import "./WorldCupModule.css";

const MONTHS_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${d} DE ${(MONTHS_ES[m - 1] || "").toUpperCase()} ${y}`;
}

function fmtShortDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const weekdays = ["dom","lun","mar","mié","jue","vie","sáb"];
  return `${weekdays[dt.getDay()]} ${d}/${m}`;
}

function useCountdown(iso) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!iso) return;
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [iso]);
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, finished: true };
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    finished: false,
  };
}

function Flag({ code, alt, size = "thumb" }) {
  if (!code) return null;
  const w = size === "big" ? 320 : 80;
  return (
    <img
      src={`https://flagcdn.com/w${w}/${code}.png`}
      alt={alt || code}
      className={`wc-flag wc-flag-${size}`}
      loading="lazy"
    />
  );
}

// ── HERO ─────────────────────────────────────────────────────
function WCHero({ data, cd }) {
  const next = data.next_match;
  return (
    <div className="wc-hero">
      <div className="wc-hero-bg" />
      <div className="wc-hero-overlay" />
      <div className="wc-hero-content">
        <div className="wc-hero-left">
          <p className="wc-hero-overline">{data.hero_overline}</p>
          <h2 className="wc-hero-title" data-testid="wc-hero-title">{data.hero_title}</h2>
          <p className="wc-hero-sub">{data.hero_subtitle}</p>
          <div className="wc-hero-stats">
            <div className="wc-hero-stat">
              <span className="wc-stat-icon">⚽</span>
              <span>{data.total_matches} partidos</span>
            </div>
            <div className="wc-hero-stat wc-countdown-inline">
              <div className="wc-cd-overline">FALTA PARA EL INICIO</div>
              <div className="wc-cd-cells">
                <div><b>{String(cd?.d ?? 0).padStart(2, "0")}</b><span>DÍAS</span></div>
                <div><b>{String(cd?.h ?? 0).padStart(2, "0")}</b><span>HORAS</span></div>
                <div><b>{String(cd?.m ?? 0).padStart(2, "0")}</b><span>MIN</span></div>
                <div><b>{String(cd?.s ?? 0).padStart(2, "0")}</b><span>SEG</span></div>
              </div>
            </div>
          </div>
        </div>

        {next && (
          <div className="wc-hero-next" data-testid="wc-next-match">
            <p className="wc-next-title">PRÓXIMO PARTIDO</p>
            <div className="wc-next-teams">
              <div className="wc-next-team">
                <Flag code={next.home_flag} alt={next.home} size="big" />
                <p>{next.home}</p>
              </div>
              <div className="wc-next-vs">VS</div>
              <div className="wc-next-team">
                <Flag code={next.away_flag} alt={next.away} size="big" />
                <p>{next.away}</p>
              </div>
            </div>
            <div className="wc-next-meta">
              <span><Calendar size={14} /> {fmtDate(next.date)}</span>
              <span className="wc-next-sep">|</span>
              <span>{next.time_bol} HRS (BOL)</span>
            </div>
            <div className="wc-next-stadium">
              <MapPin size={14} /> {next.stadium}, {next.city}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SPONSOR ─────────────────────────────────────────────────
function WCSponsor({ logo, link, label }) {
  if (!logo) return null;
  const inner = (
    <div className="wc-sponsor" data-testid="wc-sponsor">
      <span className="wc-sponsor-label">{label || "AUSPICIA"}</span>
      <img src={logo} alt="Auspiciante" className="wc-sponsor-logo" />
    </div>
  );
  return link ? <a href={link} target="_blank" rel="noopener noreferrer">{inner}</a> : inner;
}

// ── FIXTURE ─────────────────────────────────────────────────
function WCFixture({ matches }) {
  const [stageFilter, setStageFilter] = useState("Todos");
  const stages = ["Todos", "Fase de grupos", "Dieciseisavos", "Octavos", "Cuartos", "Semifinal", "Final"];
  const filtered = useMemo(() => {
    if (stageFilter === "Todos") return matches;
    return matches.filter((m) => m.stage === stageFilter);
  }, [matches, stageFilter]);

  // Group by date
  const byDate = useMemo(() => {
    const out = {};
    for (const m of filtered) {
      out[m.date] = out[m.date] || [];
      out[m.date].push(m);
    }
    return out;
  }, [filtered]);

  const dates = Object.keys(byDate).sort();
  const [page, setPage] = useState(0);
  const datesPerPage = 3;
  const pageCount = Math.ceil(dates.length / datesPerPage);
  const visibleDates = dates.slice(page * datesPerPage, (page + 1) * datesPerPage);

  return (
    <div className="wc-fixture" data-testid="wc-fixture">
      <div className="wc-section-head">
        <h3 className="wc-section-title">FIXTURE COMPLETO</h3>
      </div>
      <div className="wc-chips">
        {stages.map((s) => (
          <button
            key={s}
            onClick={() => { setStageFilter(s); setPage(0); }}
            className={`wc-chip ${stageFilter === s ? "wc-chip-active" : ""}`}
            data-testid={`wc-stage-${s}`}
          >
            {s === "Todos" ? "Todos los partidos" : s}
          </button>
        ))}
      </div>

      <div className="wc-match-list">
        {visibleDates.length === 0 && (
          <p className="wc-empty">No hay partidos para esta etapa.</p>
        )}
        {visibleDates.map((d) => (
          <div key={d} className="wc-day-group">
            <p className="wc-day-title">{fmtDate(d)}</p>
            {byDate[d].map((m) => (
              <div key={m.id} className="wc-match" data-testid={`wc-match-${m.id}`}>
                <div className="wc-match-time">
                  <p className="wc-time">{m.time_bol}</p>
                  <p className="wc-time-tz">HRS (BOL)</p>
                </div>
                <div className="wc-match-stage">
                  <span className="wc-stage-pill">{m.stage.toUpperCase()}</span>
                  <span className="wc-status">{m.status === "finished" ? "Finalizado" : m.status === "live" ? "EN VIVO" : "No iniciado"}</span>
                </div>
                <div className="wc-match-teams">
                  <div className="wc-team-row wc-team-home">
                    <Flag code={m.home_flag} alt={m.home} />
                    <span>{m.home}</span>
                  </div>
                  <div className="wc-match-vs">
                    {m.status === "finished" && m.score_home !== null
                      ? <span className="wc-score">{m.score_home} - {m.score_away}</span>
                      : <span>VS</span>}
                  </div>
                  <div className="wc-team-row wc-team-away">
                    <span>{m.away}</span>
                    <Flag code={m.away_flag} alt={m.away} />
                  </div>
                </div>
                <div className="wc-match-venue">
                  <p>{m.stadium},</p>
                  <p>{m.city}</p>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="wc-pager">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="wc-pager-btn">‹ Anterior</button>
          <span className="wc-pager-info">Página {page + 1} de {pageCount}</span>
          <button onClick={() => setPage(Math.min(pageCount - 1, page + 1))} disabled={page === pageCount - 1} className="wc-pager-btn">Siguiente ›</button>
        </div>
      )}
    </div>
  );
}

// ── GROUPS ──────────────────────────────────────────────────
function WCGroups({ groups, standings, onTeamClick }) {
  const [active, setActive] = useState(groups[0]?.group || "A");
  const activeGroup = groups.find((g) => g.group === active);
  const activeStandings = standings[active] || [];

  return (
    <div className="wc-groups" data-testid="wc-groups">
      <h3 className="wc-section-title">GRUPOS</h3>
      <div className="wc-group-tabs">
        {groups.map((g) => (
          <button
            key={g.group}
            onClick={() => setActive(g.group)}
            className={`wc-group-tab ${active === g.group ? "wc-group-tab-active" : ""}`}
            data-testid={`wc-group-${g.group}`}
          >
            Grupo {g.group}
          </button>
        ))}
      </div>

      {activeGroup && (
        <div className="wc-group-grid">
          {activeGroup.teams.map((t) => (
            <button key={t.name} type="button" onClick={() => onTeamClick && onTeamClick(t.name)} className="wc-group-card wc-team-card-btn">
              <Flag code={t.flag} alt={t.name} size="big" />
              <p>{t.name}</p>
            </button>
          ))}
        </div>
      )}

      <h3 className="wc-section-title wc-mt-lg">POSICIONES - GRUPO {active}</h3>
      <div className="wc-standings-table-wrap">
        <table className="wc-standings-table">
          <thead>
            <tr>
              <th>#</th>
              <th className="wc-left">SELECCIÓN</th>
              <th>PJ</th><th>G</th><th>E</th><th>P</th><th>DG</th><th>PTS</th>
            </tr>
          </thead>
          <tbody>
            {activeStandings.map((r, i) => (
              <tr key={r.team} data-testid={`wc-standing-${r.team}`}>
                <td>{i + 1}</td>
                <td className="wc-left wc-team-cell">
                  <Flag code={r.flag} alt={r.team} />
                  <span>{r.team}</span>
                </td>
                <td>{r.pj}</td><td>{r.g}</td><td>{r.e}</td><td>{r.p}</td>
                <td>{r.dg > 0 ? `+${r.dg}` : r.dg}</td>
                <td className="wc-pts">{r.pts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SELECCIONES ─────────────────────────────────────────────
function WCTeams({ groups, onTeamClick }) {
  const [byGroup, setByGroup] = useState(false);
  const allTeams = groups.flatMap((g) => g.teams);

  return (
    <div className="wc-teams" data-testid="wc-teams">
      <div className="wc-section-head">
        <h3 className="wc-section-title">SELECCIONES</h3>
        <div className="wc-view-toggle">
          <button onClick={() => setByGroup(false)} className={`wc-toggle-btn ${!byGroup ? "wc-toggle-active" : ""}`}>Vista general</button>
          <button onClick={() => setByGroup(true)} className={`wc-toggle-btn ${byGroup ? "wc-toggle-active" : ""}`}>Vista por grupo</button>
        </div>
      </div>

      {byGroup ? (
        <div className="wc-teams-by-group">
          {groups.map((g) => (
            <div key={g.group} className="wc-tg-block">
              <p className="wc-tg-title">Grupo {g.group}</p>
              <div className="wc-team-grid">
                {g.teams.map((t) => (
                  <button key={t.name} type="button" onClick={() => onTeamClick(t.name)} className="wc-team-card wc-team-card-btn" data-testid={`wc-team-card-${t.name}`}>
                    <Flag code={t.flag} alt={t.name} size="big" />
                    <p>{t.name}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="wc-team-grid">
          {allTeams.map((t) => (
            <button key={t.name} type="button" onClick={() => onTeamClick(t.name)} className="wc-team-card wc-team-card-btn" data-testid={`wc-team-card-${t.name}`}>
              <Flag code={t.flag} alt={t.name} size="big" />
              <p>{t.name}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── JUGADORES ───────────────────────────────────────────────
function WCPlayers({ players, teamFilter, teamFlag, onClearFilter }) {
  const filtered = teamFilter ? (players || []).filter((p) => p.team === teamFilter || p.country === teamFilter) : (players || []);
  return (
    <div className="wc-players" data-testid="wc-players">
      <div className="wc-section-head">
        <h3 className="wc-section-title">
          {teamFilter ? `JUGADORES — ${teamFilter.toUpperCase()}` : "JUGADORES DESTACADOS"}
        </h3>
        {teamFilter && (
          <button onClick={onClearFilter} className="wc-link" data-testid="wc-clear-team-filter">
            ← Ver todos los jugadores
          </button>
        )}
      </div>
      {teamFilter && teamFlag && (
        <div className="wc-team-header">
          <Flag code={teamFlag} alt={teamFilter} size="big" />
          <div>
            <p className="wc-team-header-label">SELECCIÓN</p>
            <p className="wc-team-header-name">{teamFilter}</p>
          </div>
        </div>
      )}
      {filtered.length === 0 ? (
        <p className="wc-empty">
          {teamFilter
            ? `Aún no hay jugadores cargados para ${teamFilter}. Los DTs anuncian los planteles ~1 mes antes del Mundial. El admin puede agregarlos a medida que se confirmen.`
            : "Próximamente. Agregá jugadores desde el panel admin."}
        </p>
      ) : (
        <div className="wc-player-grid">
          {filtered.map((p, i) => (
            <div key={i} className="wc-player-card" data-testid={`wc-player-${i}`}>
              <div className="wc-player-photo-wrap">
                {p.photo_url
                  ? <img src={p.photo_url} alt={p.name} className="wc-player-photo" loading="lazy" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                  : null}
                <div className="wc-player-photo wc-player-photo-placeholder" style={{ display: p.photo_url ? "none" : "flex" }}>
                  <UserCircle size={48} />
                </div>
                {p.dorsal && (
                  <div className="wc-player-dorsal">
                    <span>DORSAL</span>
                    <b>#{p.dorsal}</b>
                  </div>
                )}
              </div>
              {(p.country || p.team) && (
                <div className="wc-player-country">
                  {p.country_flag && <Flag code={p.country_flag} alt={p.country || p.team} />}
                  <span>{(p.country || p.team || "").toUpperCase()}</span>
                </div>
              )}
              <p className="wc-player-name">{p.name}</p>
              <div className="wc-player-stats">
                {p.age && <div><span>EDAD</span><b>{p.age} años</b></div>}
                {p.position && <div><span>POSICIÓN</span><b>{p.position}</b></div>}
                {p.height && <div><span>ALTURA</span><b>{p.height} cm</b></div>}
                {p.weight && <div><span>PESO</span><b>{p.weight} kg</b></div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── NOTICIAS ────────────────────────────────────────────────
function WCNews({ articles }) {
  if (!articles || articles.length === 0) {
    return (
      <div className="wc-news" data-testid="wc-news-empty">
        <h3 className="wc-section-title">NOTICIAS DEL MUNDIAL</h3>
        <p className="wc-empty">Aún no hay notas. Publicá artículos en la categoría Deportes y aparecerán acá automáticamente.</p>
      </div>
    );
  }
  return (
    <div className="wc-news" data-testid="wc-news">
      <div className="wc-section-head">
        <h3 className="wc-section-title">NOTICIAS DEL MUNDIAL</h3>
        <Link to="/categoria/deportes" className="wc-link">Ver todas las noticias →</Link>
      </div>
      <div className="wc-news-grid">
        {articles.slice(0, 3).map((a) => (
          <Link key={a.id} to={`/nota/${a.slug}`} className="wc-news-card" data-testid={`wc-news-${a.id}`}>
            <div className="wc-news-img-wrap">
              {a.cover_image && <img src={a.cover_image} alt={a.title} loading="lazy" />}
            </div>
            <div className="wc-news-info">
              <p className="wc-news-cat">{(a.category_slug || "DEPORTES").toUpperCase()} <span className="wc-news-time">· {timeAgo(a.published_at || a.created_at)}</span></p>
              <p className="wc-news-title">{a.title}</p>
              <p className="wc-news-excerpt">{a.excerpt || ""}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── MAIN MODULE ─────────────────────────────────────────────
const TABS = [
  { k: "fixture", label: "Fixture", icon: Calendar },
  { k: "grupos", label: "Grupos", icon: ListChecks },
  { k: "selecciones", label: "Selecciones", icon: Users },
  { k: "jugadores", label: "Jugadores", icon: UserCircle },
  { k: "noticias", label: "Noticias", icon: Newspaper },
];

export default function WorldCupModule({ articles = [] }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("fixture");
  const [teamFilter, setTeamFilter] = useState(null);
  useEffect(() => {
    api.get("/worldcup").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const cd = useCountdown(data?.kickoff_iso);

  if (!data || !data.enabled) return null;

  const handleTeamClick = (teamName) => {
    setTeamFilter(teamName);
    setTab("jugadores");
    // Scroll into module top smoothly
    setTimeout(() => {
      const el = document.querySelector('[data-testid="worldcup-module"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 30);
  };

  const allTeams = (data.groups || []).flatMap((g) => g.teams);
  const filterFlag = teamFilter ? (allTeams.find((t) => t.name === teamFilter)?.flag) : null;

  return (
    <section className="wc-module" data-testid="worldcup-module">
      <WCHero data={data} cd={cd} />

      <div className="wc-tabs">
        {TABS.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => { setTab(k); if (k !== "jugadores") setTeamFilter(null); }}
            className={`wc-tab ${tab === k ? "wc-tab-active" : ""}`}
            data-testid={`wc-tab-${k}`}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <WCSponsor logo={data.sponsor_logo_url} link={data.sponsor_link} label={data.sponsor_label} />

      <div className="wc-panel">
        {tab === "fixture" && (
          <div className="wc-row-2">
            <div className="wc-col-main"><WCFixture matches={data.matches} /></div>
            <div className="wc-col-side"><WCGroups groups={data.groups} standings={data.standings} onTeamClick={handleTeamClick} /></div>
          </div>
        )}
        {tab === "grupos" && <WCGroups groups={data.groups} standings={data.standings} onTeamClick={handleTeamClick} />}
        {tab === "selecciones" && <WCTeams groups={data.groups} onTeamClick={handleTeamClick} />}
        {tab === "jugadores" && (
          <WCPlayers
            players={data.featured_players}
            teamFilter={teamFilter}
            teamFlag={filterFlag}
            onClearFilter={() => setTeamFilter(null)}
          />
        )}
        {tab === "noticias" && <WCNews articles={articles} />}
      </div>
    </section>
  );
}
